import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { formatPrice } from '../../utils/priceFormatter';
import { API_BASE } from '../../utils/url';
import { downloadQuotationPDF } from '../../utils/pdfGenerator';
import { useToast } from '../../contexts/ToastContext';

const QuotationPublicPage = () => {
  const { quotationNumber } = useParams();
  const { showError } = useToast();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get((process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + `/quotations/public/number/${quotationNumber}`);
        setQuotation(res.data);
      } catch (e) {
        setQuotation(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [quotationNumber]);

  const handleDownload = async () => {
    if (!quotation) return;
    setDownloading(true);
    try {
      const items = quotation.items || [];
      await downloadQuotationPDF(quotation, items);
    } catch (error) {
      console.error('Error generating PDF:', error);
      showError('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div style={{ padding: 20 }}>Loading…</div>;
  if (!quotation) return <div style={{ padding: 20 }}>Quotation not found.</div>;

  const rows = quotation.items || [];
  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN');

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      {/* Header section like provided template */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: 16 }}>
        {/* Download Button */}
        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <button
            onClick={handleDownload}
            disabled={downloading}
            style={{
              background: downloading ? '#ccc' : '#000',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 20px',
              fontSize: 14,
              fontWeight: 600,
              cursor: downloading ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            {downloading ? 'Generating PDF...' : 'Download PDF'}
            {!downloading && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            )}
          </button>
        </div>
        <div style={{ border: '2px solid #000', padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 800, fontSize: 16 }}>VENZO WOOD PRIVATE LIMITED</div>
            <div style={{ fontSize: 12, textAlign: 'right' }}>+918080282228</div>
          </div>
          <div style={{ fontSize: 12, marginTop: 2 }}>Andheri, Lower Parel, Surat, Nashik, Hyderabad</div>
          <div style={{ marginTop: 10, fontWeight: 700, textAlign: 'center' }}>Quotation</div>
          <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 12 }}>
            <div>Date: {formatDate(quotation.created_at)}</div>
            <div>Ref No.: {quotation.quotation_number}</div>
          </div>

          {/* Meta row */}
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 6, marginTop: 10, fontSize: 12 }}>
            <div>sales</div><div>{quotation.prepared_by || '-'}</div>
            <div>Prepared by</div><div>{quotation.prepared_by || '-'}</div>
            <div>Project</div><div>{quotation.project || '-'}</div>
            <div>Confirmed By</div><div>{quotation.confirmed_by || '-'}</div>
          </div>

          {/* Customer details */}
          <div style={{ marginTop: 10, fontWeight: 700, fontSize: 12 }}>Customer details</div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 6, fontSize: 12 }}>
            <div>Client Name</div><div>{quotation.customer_name}</div>
            <div>Email ID</div><div>{quotation.customer_email}</div>
            <div>Address</div><div>{quotation.customer_address || '-'}</div>
            <div>GSTIN No</div><div>{quotation.customer_gstin || '-'}</div>
            <div>Arch Name</div><div>{quotation.architect_name || '-'}</div>
            <div>Tel</div><div>{quotation.customer_phone}</div>
          </div>

          {/* Items table */}
          <div style={{ marginTop: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={th}>Ref No</th>
                  <th style={th}>Qty in Sheet</th>
                  <th style={th}>Description</th>
                  <th style={th}>Item</th>
                  <th style={th}>Price Per Sqft</th>
                  <th style={th}>Total price (Rs.)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((it, idx) => (
                  <tr key={idx}>
                    <td style={td}>{idx + 1}</td>
                    <td style={td}>{it.quantity}</td>
                    <td style={td}>{it.product_name}</td>
                    <td style={td}>—</td>
                    <td style={td}>{formatPrice(it.unit_price)}</td>
                    <td style={td}>{formatPrice(it.total_price)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={5} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Total</td>
                  <td style={{ ...td, fontWeight: 700 }}>{formatPrice(quotation.total_amount)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Terms */}
          <div style={{ marginTop: 12, fontSize: 11 }}>
            <ol style={{ margin: 0, paddingLeft: 16, lineHeight: 1.5 }}>
              <li>Rates are delivery ex mumbai</li>
              <li>Payment 100% advance. Delivery in IMM after receipt of full payment for fluting.</li>
              <li>Any changes in Government Policy will be applicable at time of delivery.</li>
              <li>Goods once sold should not be taken back. Orders placed cannot be cancelled or exchanged.</li>
              <li>Quality inspection at our end shall be treated as final.</li>
              <li>Issue a Purchase Order to organise delivery of the goods.</li>
              <li>Issue a cheque for 100% of the amount in favour of VENZO WOOD PRIVATE LIMITED.</li>
              <li>As veneers are natural items no guarantee/warranty for colour & figure variations.</li>
              <li>Any dispute subject to Mumbai jurisdiction.</li>
              <li>Our Delivery Ex-warehouse.</li>
              <li>Kindly note our GST No. 27AAHCV3921F1ZC</li>
            </ol>
          </div>

          {/* Bank details */}
          <div style={{ marginTop: 10, fontSize: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Bank details</div>
            <div>Company Name: VENZO WOOD PRIVATE LIMITED</div>
            <div>Bank: HDFC Bank</div>
            <div>Account Number: 50200093579707</div>
            <div>IBAN Number: HDFC0000668</div>
            <div>BRANCH: Vile Parla East</div>
            <div>Currency: INR</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const th = { border: '1px solid #000', padding: 6, textAlign: 'left' };
const td = { border: '1px solid #000', padding: 6 };

export default QuotationPublicPage;

